import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../../../../store/slices/authSlice';
import api from '../../../../api/axios';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates, calculateAge, getCountryCodes } from '../../../../utils/common';
import useAuth from '../../../../hooks/useAuth';
import { useLoading } from '../../../../components/LoadingProvider';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getPublicImgUrl } from '../../../../utils/common';

export default function PatientProfileEdit() {
    document.title = 'Varosa - Patient Profile';
    const fetchedRef = useRef(false);
    const { user, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['patient'] });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const indStates = getIndStates();
    const countryCodes = getCountryCodes();
    const { setIsLoading } = useLoading();

    const MySwal = withReactContent(Swal);

    const [previewUrl, setPreviewUrl] = useState(getPublicImgUrl() + '/simg.jpg');
    const fileInputRef = useRef(null);
    const reportFileInputRef = useRef(null);
    const errorDivRef = useRef(null);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [prefixOfName, setPrefixOfName] = useState([ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ]);
    const [errors, setErrors] = useState({
        profile_pic: '',
        name: '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            district: '',
            state: '',
            country: '',
            pincode: ''
        },
        email: '',
    });

    const [formData, setFormData] = useState({
        name: user?.name || '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            line2: '',
            district: '',
            state: '',
            country: 'India',
            pincode: ''
        },
        email: '',
        phoneCode: '',
        phoneNumber: '',
        contactNumber: user?.phone || '',
        allergy: '',
        regularMedicines: '',
        previousReports: []
    });

    const validateForm = () => {
        let all_good = true;
        let error = {
            profile_pic: '',
            namePrefix: '',
            name: '',
            dob: '',
            gender: '',
            address: {
                line1: '',
                district: '',
                state: '',
                country: '',
                pincode: ''
            },
            email: '',
        };

        if(formData.namePrefix.trim() === ''){
            error.namePrefix = 'Please select a valid prefix.';
            all_good = false;
        }
        if(formData.name.trim() === '' || formData.name.trim().length < 3){
            error.name = 'Please enter a valid name.';
            all_good = false;
        }
        if(formData.dob.trim() === ''){
            error.dob = 'Please enter a valid DOB.';
            all_good = false;
        }
        if(formData.gender.trim() === ''){
            error.dob = 'Please select a valid gender.';
            all_good = false;
        }
        if(formData.address.line1.trim() === ''){
            error.address.line1 = 'Please enter a valid address.';
            all_good = false;
        }
        if(formData.address.district.trim() === ''){
            error.address.district = 'Please enter a valid district.';
            all_good = false;
        }
        if(formData.address.state.trim() === ''){
            error.address.state = 'Please select a valid state.';
            all_good = false;
        }
        if(formData.address.country.trim() === ''){
            error.address.country = 'Please enter a valid country.';
            all_good = false;
        }
        if(formData.address.pincode.trim().replace(/\D/g, '').length !== 6){
            error.address.pincode = 'Please enter a valid pincode (6-digits).';
            all_good = false;
        }
        if(formData.email.trim() !== '' && !/^\S+@\S+\.\S+$/.test(formData.email.trim())){
            error.email = 'Please enter a valid email.';
            all_good = false;
        }
        
        setErrors(error);
        return all_good;
    };

    const fetchPatientData = useCallback(async () => {
        setBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/details');
            if (response?.data?.result === 'success') {
                setFormData({
                    namePrefix: response.data.data.name_prefix || '',
                    name: response.data.data.name || '',
                    dob: response.data.data.dob || '',
                    gender: response.data.data.gender || '',
                    address: {
                        line1: response.data.data.address_line1 || '',
                        line2: response.data.data.address_line2 || '',
                        district: response.data.data.district || '',
                        state: response.data.data.state || '',
                        country: response.data.data.country || '',
                        pincode: response.data.data.pincode || '',
                    },
                    email: response.data.data.email || '',
                    phoneCode: response.data.data.phone_code || '',
                    phoneNumber: response.data.data.phone_number || '',
                    contactNumber: '',
                    allergy: response.data.data.allergies || '',
                    regularMedicines: response.data.data.regular_medicines || '',
                    previousReports: response.data.data.documents || [],
                });
                setPrefixOfName(response.data.data.prefix_of_name);
                setPreviewUrl(response.data.data.profile_picture);
                dispatch(setUser({
                    name: response.data.data.name_prefix+' '+response.data.data.name,
                    email: response.data.data.email || '',
                    phone_code: response.data.data.phone_code || '',
                    phone_number: response.data.data.phone_number || '',
                    profile_pic: response.data.data.profile_picture || '',
                }));
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setBtnDisabled(false);
        }
    }, []);
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        fetchPatientData();
    }, [fetchPatientData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        for(let file of files){
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                showError('Please upload a PDF, PNG, JPEG file.');
                reportFileInputRef.current.value = '';
                return;
            }
            if(file.size > 2 * 1024 * 1024){
                showError('File size exceeds 2MB. Please choose a smaller file.');
                reportFileInputRef.current.value = '';
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            previousReports: ((Array.isArray(prev.previousReports) ? [...prev.previousReports, ...files] : [...files]) || []),
        }));
        reportFileInputRef.current.value = '';
    };

    const handleRemoveFile = (index) => {
        const updatedReports = formData.previousReports.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            previousReports: updatedReports,
        }));
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const viewReport = async (report, isItUrl = false) => {
        try{
            if(isItUrl){
                MySwal.fire({
                    title: 'Report',
                    html: `<iframe src="${report}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                    width: '90%',
                    showConfirmButton: true,
                    confirmButtonText: 'Close',
                    customClass: {
                        container: '',
                    },
                });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result;
                if(base64String){
                    MySwal.fire({
                        title: 'Report',
                        html: `<iframe src="${base64String}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                        width: '90%',
                        showConfirmButton: true,
                        confirmButtonText: 'Close',
                        customClass: {
                            container: '',
                        },
                    });
                }
            };
            reader.readAsDataURL(report);
        }catch(error){
            console.log(error);
            showError('Something went wrong! Please try again later.');
            return;
        }
    };

    const handleProfilePicUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setErrors(prev => ({
            ...prev,
            profile_pic: ''
        }));

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            setErrors(prev => ({
                ...prev,
                profile_pic: 'Please select a valid image file (JPEG, JPG, or PNG).'
            }));
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                profile_pic: 'File size exceeds 2MB. Please choose a smaller file.'
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            profile_pic: file,
        }));

        // Create preview
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleResetProfilePicUpload = () => {
        setPreviewUrl(getPublicImgUrl() + '/simg.jpg');
        setFormData(prev => ({
            ...prev,
            profile_pic: '',
        }));
        setErrors(prev => ({
            ...prev,
            profile_pic: ''
        }));
        fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if(!validateForm()){ 
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return; 
            }

            const postData = new FormData();
            // Add all form fields to postData
            postData.append('name_prefix', formData.namePrefix);
            postData.append('name', formData.name);
            postData.append('dob', formData.dob);
            postData.append('gender', formData.gender);
            postData.append('address_line1', formData.address.line1);
            postData.append('address_line2', formData.address.line2);
            postData.append('district', formData.address.district);
            postData.append('state', formData.address.state);
            postData.append('country', formData.address.country);
            postData.append('pincode', formData.address.pincode);
            postData.append('email', formData.email);
            postData.append('allergies', formData.allergy);
            postData.append('regular_medicines', formData.regularMedicines);
            if (formData.profile_pic) {
                postData.append('profile_picture', formData.profile_pic);
            }
            if (Array.isArray(formData.previousReports)) {
                for(let report of formData.previousReports){
                    if(report.id){
                        postData.append('existing_previous_reports_ids[]', report.id);
                    }else{
                        postData.append('previous_reports[]', report);
                    }
                }
                // postData.append('previous_reports', formData.previousReports);
            }

            setBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/details', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                fetchPatientData();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setBtnDisabled(false);
            setIsLoading(false);
        }
    };

    return (
        <div className="container py-5 login-container">
            <div className="row g-3">
                <div className="col-9">
                    <h2 className="mb-4">My Profile</h2>
                </div>
                <div className="col-3 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="needs-validation" ref={errorDivRef}>
                <div className="row g-3">
                    <div className="col-md-12">
                        <div className="form-group text-center">
                            <div className="img-preview-container mb-3">
                                {previewUrl && <img id="imagePreview" src={previewUrl} className="img-thumbnail" width={300} alt="Profile" />}
                            </div>
                            <input type="file" id="profilePicInput" accept="image/*" className="d-none" ref={fileInputRef} onChange={handleProfilePicUpload} />
                            <button type="button" className="btn btn-outline-primary mx-2 mt-2" onClick={() => fileInputRef.current.click()}>
                                <i className="fas fa-upload mr-2"></i> Upload Profile Picture
                            </button>
                            <button type="button" className="btn btn-outline-danger mx-2 mt-2" onClick={handleResetProfilePicUpload}>
                                <i className="fas fa-times mr-2"></i> Reset Profile Picture
                            </button>
                            <div className="text-muted mt-2">
                                <small>JPEG, JPG, or PNG. Max size 2MB.</small>
                            </div>
                            {errors.profile_pic && <div className="invalid-feedback d-block">{errors.profile_pic}</div>}
                        </div>
                    </div>
                    {/* Basic Information */}
                    <div className="col-md-6">
                        <label className="form-label">Name</label>
                        <div className="input-group mb-3">
                            <select 
                                className="form-select py-3 border-primary"
                                name="namePrefix"
                                value={formData.namePrefix}
                                onChange={handleInputChange}
                                style={{ maxWidth: '120px' }}
                            >
                                <option>Choose...</option>
                                {prefixOfName.map((prefix) => (
                                    <option key={prefix} value={prefix}>{prefix}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        {errors.name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                        {errors.namePrefix && <div className="invalid-feedback d-block">{errors.namePrefix}</div>}
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">DOB</label>
                        <input
                            type="date"
                            className="form-control py-3 border-primary"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                            required
                            max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.dob && <div className="invalid-feedback d-block">Please enter a valid DOB.</div>}
                    </div>
                    <div className="col-md-3">
                        <label className="form-label">Gender</label>
                        <select
                            className="form-select py-3 border-primary"
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Choose...</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                        {errors.gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                    </div>

                    {/* Address Fields */}
                    <div className="col-md-6">
                        <label className="form-label">Address Line 1</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.line1"
                            value={formData.address.line1}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Address Line 2 (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.line2"
                            value={formData.address.line2}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">District</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.district"
                            value={formData.address.district}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">State</label>
                        <select
                            className="form-select py-3 border-primary"
                            name="address.state"
                            value={formData.address.state}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Choose...</option>
                            {indStates.map((val) => (
                                <option key={val.key} value={val.key}>
                                    {val.name}
                                </option>
                            ))}
                        </select>
                        {errors.address.state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                        {/* <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.state"
                            value={formData.address.state}
                            onChange={handleInputChange}
                            required
                        /> */}
                    </div>
                    {/* <div className="col-md-4">
                        <label className="form-label">Country</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.country"
                            value={formData.address.country}
                            onChange={handleInputChange}
                            required
                            readOnly 
                        />
                        {errors.address.country && <div className="invalid-feedback d-block">Please enter a valid country.</div>}
                    </div> */}
                    <div className="col-md-4">
                        <label className="form-label">Pincode</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.pincode"
                            value={formData.address.pincode}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                    </div>

                    {/* Contact Information */}
                    <div className="col-md-6">
                        <label className="form-label">Email ID (optional)</label>
                        <input
                            type="email"
                            className="form-control py-3 border-primary"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                        {errors.email && <div className="invalid-feedback d-block">Please enter a valid email.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Contact Number</label>
                        <div className="input-group mb-3">
                            <select 
                                className="form-select py-3 border-primary"
                                name="phoneCode"
                                value={formData.phoneCode}
                                onChange={handleInputChange}
                                style={{ maxWidth: '120px' }}
                                disabled={true}
                            >
                                <option>Choose...</option>
                                {countryCodes.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.code}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                className="form-control py-3 border-primary"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                readOnly
                            />
                        </div>
                    </div>

                    {/* Medical Information */}
                    <div className="col-12">
                        <label className="form-label">Allergies (optional)</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="allergy"
                            value={formData.allergy}
                            onChange={handleInputChange}
                            rows="3"
                        ></textarea>
                        {errors.allergy && <div className="invalid-feedback d-block">Please enter a valid allergy.</div>}
                    </div>
                    <div className="col-12">
                        <label className="form-label">Regular Medicine List (optional)</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="regularMedicines"
                            value={formData.regularMedicines}
                            onChange={handleInputChange}
                            rows="3"
                        ></textarea>
                        {errors.regularMedicines && <div className="invalid-feedback d-block">Please enter a valid regular medicine list.</div>}
                    </div>

                    {/* File Upload */}
                    <div className="col-12">
                        <label className="form-label">Previous Reports and Prescriptions (optional)</label>
                        <input
                            type="file"
                            className="form-control py-3 border-primary"
                            onChange={handleFileUpload}
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                            ref={reportFileInputRef}
                        />
                        <div className="text-muted mt-2 mb-2">
                            <small>PDF, JPEG, JPG, or PNG. Max size 2MB per file.</small>
                        </div>
                        {formData.previousReports.length > 0 && (
                            <div className="mt-2">
                                <p>Selected files:</p>
                                <ul className="list-group">
                                    {formData.previousReports.map((file, index) => (
                                        file.id ? 
                                        (<li key={index} className="list-group-item">
                                            {file.original_name} ({formatBytes(file.file_size)})
                                            <span className="float-end">
                                                <i 
                                                    className="fas fa-eye text-primary me-3" 
                                                    onClick={() => viewReport(file.file_path, true)}
                                                    style={{cursor: 'pointer'}}></i>
                                                <i 
                                                    className="fas fa-trash-alt text-danger" 
                                                    onClick={() => handleRemoveFile(index)} 
                                                    style={{cursor: 'pointer'}}></i>
                                            </span>
                                        </li>)
                                        : (<li key={index} className="list-group-item">
                                            {file.name} ({formatBytes(file.size)})
                                            <span className="float-end">
                                                <i 
                                                    className="fas fa-eye text-primary me-3" 
                                                    onClick={() => viewReport(file, false)}
                                                    style={{cursor: 'pointer'}}></i>
                                                <i 
                                                    className="fas fa-trash-alt text-danger" 
                                                    onClick={() => handleRemoveFile(index)} 
                                                    style={{cursor: 'pointer'}}></i>
                                            </span>
                                        </li>)
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-4">
                    <button type="submit" className="btn btn-primary" disabled={btnDisabled}>Save Profile</button>
                </div>
            </form>
        </div>
    );
}